import React, { useContext, useEffect, useRef, useState } from 'react';
import postal from 'postal';
import TableEditContext from './tableEditContext';
import useEditableTableNavigation from '../useEditableTableNavigation';
import _ from 'lodash';
import { ApplicableTableSettings } from '../../../tableSettings';
import useDeepCompareEffect from '../../../hooks/useDeepCompareEffect';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { isEveryPropertyEmpty } from '../../../util';

export type TableEditControlsHandle = {
  setTableReference: (tableReference: HTMLDivElement) => void,
  add: (focusRowIndex?: number) => any,
};

type TableEditControlsProps = {
  tableOptions: ApplicableTableSettings,
  rowAddActionRef: React.MutableRefObject<'append' | 'insert' | null>;
}

const TableEditControlsImpl: React.ForwardRefRenderFunction<TableEditControlsHandle, TableEditControlsProps> = (props, ref) => {
  const { tableOptions, rowAddActionRef  } = props;
  const tableIdRef = useRef('');

  let enterSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    enterSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.editable-table-onEnter',
      callback: (data = { backFocus: false, changeFocus: true, element: HTMLElement }) => {
        onEnter(data.backFocus, data.changeFocus, data.element);
      },
    });
  }

  function unsubscribeChannels() {
    if (enterSubscription) {
      postal.unsubscribe(enterSubscription);
    }
  }
  const [tableId, _setTableId] = useState('');
  const [tick, setTick] = useState(0); // To be able to rerender based on observer changes

  const observer = useRef<MutationObserver | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>();

  const tableEditContext = useContext(TableEditContext);
  //const [tempEditColumnIndex, setTempEditColumnIndex] = useState(tableEditContext.editColumnIndex); //Edit Mode일 때의 Edit Col Index
  const tableDataLength = tableEditContext.data.length;
  const tableParentDivId = tableEditContext.tableParentDivId;
  const { clearTableSelection } = useEditableTableNavigation(tableOptions.tableName, tableEditContext, onDelete, add, tableContainerRef.current, tableOptions.useVirtual, rowAddActionRef );
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  function setTableId(id: string) {
    tableIdRef.current = id;
    _setTableId(id);
  }

  React.useImperativeHandle(ref, () => ({
    setTableReference,
    add,
  }));

  useEffect(() => {
    if (tableEditContext.initializeFocusedCell && tableEditContext.initializeFocusedCell.focus && tableEditContext.initializeFocusedCell.dataLength > 0 ) {
      setTimeout(() => {
        setSelectedCol(1);
        setSelectedRow(0);
      }, 10);
      tableEditContext.initializeFocusedCell.focus = false;
    }
  }, [tableEditContext.initializeFocusedCell?.focus, tableEditContext.initializeFocusedCell?.dataLength])

  useDeepCompareEffect(() => {
    initialize();
    return uninitialize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParentDivId, tableEditContext, tableId]);

  function setSelectedCol(c: number) {
    tableEditContext.setEditColumnIndex(c);
  }

  function setSelectedRow(c: number) {
    tableEditContext.setEditRowIndex(c);
  }

  function initialize() {
    subscribeChannels();
  }

  function uninitialize() {
    unsubscribeChannels();
    uninitializeObserver();
  }

  function initializeObserver(targetNode: HTMLDivElement) {
    if (!targetNode) {
      return;
    }

    if (observer && observer.current) {
      uninitializeObserver();
    }

    const observerConfig = {
      attributes: true,
      childList: true,
      subtree: true,
    };

    observer.current = new MutationObserver(observe);
    observer.current.observe(targetNode, observerConfig);

    // Run initially for getting initial values
    observe();
  }

  function uninitializeObserver() {
    if (observer && observer.current) {
      observer.current.disconnect();
    }
  }

    function isScrolledIntoView(container: HTMLElement, row: HTMLElement) {
    const trect = container.getBoundingClientRect();
    const rect = row.getBoundingClientRect();

    const viewStart = trect.top;
    const viewEnd = trect.top + trect.height;

    return rect.top - 20 >= viewStart && (rect.top + rect.height) <= viewEnd; //20 = header height
  }

  function makeSureControlsAreScrolledIntoView() {
    if (!tableContainerRef.current) {
      return;
    }

    const tableNode = tableContainerRef.current.querySelector('div[role="table"]') as HTMLElement;
    if (!tableNode) {
      return;
    }

    const controlsInTable = tableContainerRef.current.querySelector('.data-row-selected')?.parentElement as HTMLElement;
    if (!controlsInTable) {
      return;
    }

    if (!isScrolledIntoView(tableNode, controlsInTable)) {
      controlsInTable.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  
  function observe() {
    if (!tableContainerRef.current) {
      return;
    }

    const tableNode = tableContainerRef.current.querySelector('div[role="table"]');
    if (tableNode && tableNode.id && tableNode.id !== tableId) {
      setTableId(tableNode.id);
      setTick(tick + 1);
    }
  }

  function setTableReference(container: HTMLDivElement) {
    if (container) {
      tableContainerRef.current = container;
      initializeObserver(container);
    }
  }

  async function onDelete(editRowIndex: number, deleteRange: number[]) {
    const canDelete = tableEditContext.canDeleteRow ? tableEditContext.canDeleteRow(editRowIndex) : true;
    const useRangeDelete = deleteRange.length > 0;
    const isRangeDeleteValid = useRangeDelete || editRowIndex >= 0;
    const isSingleDeleteValid = !useRangeDelete && editRowIndex >= 0;

    if ((!isSingleDeleteValid && !isRangeDeleteValid) || !tableEditContext.enabled || !canDelete) {
      if (tableIdRef.current === 'companyGroup-id-list') {
        return await showGlobalOkDialog(
          <div>
            <h3>You can't change Main Type!</h3>
          </div>
        );
      }
      return;
    }

    const editablePropertyNames = tableOptions.rowRenderers
      .filter((x: any) => !!x.EditCell)
      .map((x: any) => x.accessor);

    const shouldConfirmDelete = (rowIndex: number): boolean => {
      if (tableEditContext.shouldConfirmBeforeDelete) {
        return tableEditContext.shouldConfirmBeforeDelete(rowIndex);
      }

      return !isEveryPropertyEmpty(
        _.pick(tableEditContext.data[rowIndex], editablePropertyNames)
      );
    };

    let deleted = false;


    if (shouldConfirmDelete(editRowIndex)) {
      let confirmed = false;

      confirmed = await showGlobalYesNoDialog(
        <div>
          Do you want to delete line item?
        </div>
      );
      if (confirmed)
        deleted = await tableEditContext.deleteLine(editRowIndex, deleteRange);
    }
    else {
      deleted = await tableEditContext.deleteLine(editRowIndex, deleteRange);
    }
    
    if (deleted) {
      if (tableDataLength === 1 || deleteRange.length === tableDataLength) {
        add(0, tableEditContext.editColumnIndex);
      } else if (editRowIndex === tableDataLength - 1) {
        setSelectedRow(editRowIndex - 1);
      }
    }
  }

  function onEnter(backFocus: boolean, changeFocus: boolean, element: HTMLElement) {
    if (!tableContainerRef.current?.contains(element)) return;

    tableEditContext.setTempEditColumnIndex(-1);
    const curEditColIndex = tableEditContext.editColumnIndex;
    if (curEditColIndex === -1) return;

    const lastIndex = tableEditContext.tableColumnLength - 1;

    const shouldMoveColumn = changeFocus && ((backFocus && curEditColIndex !== 0) || (!backFocus && curEditColIndex !== lastIndex));

    if (shouldMoveColumn) {
      const newIndex = backFocus ? curEditColIndex - 1 : curEditColIndex + 1;
      setSelectedCol(newIndex);
    }
    else {
      const cellId = `${tableId}-cell-${tableEditContext.editRowIndex}-${curEditColIndex}`;
      const cell = document.getElementById(cellId);

      if (cell) {
        cell.classList.add('data-cell-selected');
      }
    }

    document.getElementById(tableId)?.focus();
    return true;
  }

  function getFirsFocusableColumnIndex(rowIndex: number): number {
    const tableNode = tableContainerRef.current as HTMLDivElement;
    let firstSelectableColumnIndex = 0;

    for (; firstSelectableColumnIndex < tableEditContext.tableColumnLength; firstSelectableColumnIndex++) {
      const editCellClass = `.ifms-editable-cell-${rowIndex}-${firstSelectableColumnIndex}`;
      const editCell = tableNode.querySelector<HTMLDivElement>(editCellClass);

      if (!editCell) {
        continue;
      }
      const hasDisabledChild = Array.from(editCell.children).some(child =>
        child.classList.contains('table-cell-disabled')
      );
      if (!hasDisabledChild) {
        break;
      }
    }

    return firstSelectableColumnIndex;
  }

  function add(focusRowIndex?: number, focusColIndex?: number) {
    if (!tableEditContext.enabled || (tableEditContext.canAdd !== undefined && !tableEditContext.canAdd)) return;
    let added = false;

    const result = tableEditContext.addLine(focusRowIndex ?? -1);
    if (result) {
      const { setDataFunc, newData, curListData } = result;
      let tempCurListData = _.cloneDeep(curListData);

      if (focusRowIndex !== undefined) {
        tempCurListData.splice(focusRowIndex, 0, newData);
      } else {
        tempCurListData.push(newData);
      }
      clearTableSelection();
      setDataFunc(tempCurListData);
      added = true;

      setTimeout(() => {
        if (added) {
          const rowCount = tableEditContext.rowCount - 1;
          setSelectedRow(focusRowIndex === undefined ? rowCount : focusRowIndex);
          setSelectedCol(focusColIndex === undefined || focusColIndex === -1 ? getFirsFocusableColumnIndex(focusRowIndex === undefined ? rowCount : focusRowIndex) : focusColIndex);
        }
      }, 10);
    }

    return added;
  }

  function renderControlsBelowEditRow() {
    return (
      <></>
    );
  }

  return renderControlsBelowEditRow();
};

const TableEditControls = React.forwardRef(TableEditControlsImpl);

export default React.memo(TableEditControls);